import { Box, Paper, Stack, Typography } from "@mui/material"
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { TSale } from "../../../../core/types/Sale"
import TextFieldRequired from "../../../Inputs/TextFieldRequired"
import Validations from "../../../../core/functions/validations"
import { numberOnly } from "../../../../masks/masks"
import React from "react"
import { useCheckoutContext } from "../../../../core/hooks/useCheckoutContext"
import { green } from "@mui/material/colors"
import { useGatewayContext } from "../../../../core/contexts/GatewayContext"
import CountrySelect from "../../../Checkout/CountrySelect"

interface ICheckoutClient8 {
  control: Control<TSale, any>
  setValue: UseFormSetValue<TSale>
  watch: UseFormWatch<TSale>
  saveLostSale: () => void
  clearErrors: () => void
}

const CheckoutClient8 = ({
  control,
  setValue,
  watch,
  saveLostSale,
  clearErrors,
}: ICheckoutClient8) => {
  const { productPrice } = useCheckoutContext()
  const { useStripe: useStripePayment, buyerCountry, setBuyerCountry } = useGatewayContext()

  const phoneValue = watch("phone")

  const customFields = productPrice?.customFields?.isCustomFields
    ? productPrice?.customFields?.fields.filter((field) => field.isChecked) ??
      []
    : []

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #DCDDDE",
        borderRadius: "20px",
        backgroundColor: "#ffffff",
      }}
    >
      <Stack direction="column" spacing={1.5}>
        {productPrice?.internationalSalesEnabled === true && (
          <CountrySelect value={buyerCountry} onChange={setBuyerCountry} />
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ pb: 0.5, pl: 1 }}
        >
          <Typography
            fontSize="24px"
            fontWeight={600}
            lineHeight="28px"
            color="#343948"
          >
            Contato
          </Typography>
        </Stack>
        <TextFieldRequired
          control={control}
          borderRadius={16}
          fieldName="email"
          label="E-mail"
          placeholder="Seu e-mail"
          onFieldExit={saveLostSale}
          clearErrors={clearErrors}
          validate={(value) =>
            !value || value == "" ? true : Validations.email(value)
          }
          inputProps={{
            "data-cy": "customerEmail",
            "data-testid": "customerEmail",
            style: { textTransform: "lowercase" },
          }}
          dataCyErrorLabel="customerEmailError"
          scheme="greenish"
          overrides={{ default: green[500], baseWidthDefault: 2 }}
        />

        <TextFieldRequired
          control={control}
          borderRadius={16}
          fieldName="name"
          label="Nome Completo"
          placeholder="Seu nome completo"
          onFieldExit={saveLostSale}
          clearErrors={clearErrors}
          inputProps={{
            "data-cy": "customerName",
            "data-testid": "customerName",
          }}
          dataCyErrorLabel="customerNameError"
          scheme="greenish"
          transform={(v) => v.replace(/[0-9]/g, "")}
          validate={(v) => {
            const s = String(v ?? "")
            return /\d/.test(s) ? "O nome não pode conter números." : true
          }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
        >
          <TextFieldRequired
            control={control}
            borderRadius={16}
            fieldName="phone"
            label="Celular"
            placeholder="(__) _____-____"
            shrink={phoneValue !== ""}
            inputProps={{
              inputMode: "numeric",
              maxLength: 15,
              "data-cy": "customerPhone",
              "data-testid": "customerPhone",
            }}
            sxInputLabel={{
              ml: phoneValue === "" ? 13.5 : 3.5,
            }}
            dataCyErrorLabel="customerPhoneError"
            onFieldExit={saveLostSale}
            clearErrors={clearErrors}
            validate={(value) =>
              !value || value == "" ? true : numberOnly(value).length === 11
            }
            scheme="greenish"
          />

          <Box sx={{ display: { xs: "block", sm: "none" } }} />

          {!useStripePayment && (
            <TextFieldRequired
              control={control}
              borderRadius={16}
              fieldName="document"
              label="CPF"
              placeholder="999.999.999-99"
              inputProps={{
                inputMode: "numeric",
                maxLength: 18,
                "data-cy": "customerDocument",
                "data-testid": "customerDocument",
              }}
              dataCyErrorLabel="customerDocumentError"
              clearErrors={clearErrors}
              validate={(value) =>
                !value || value == "" ? true : Validations.CPF_CNPJ(value)
              }
              scheme="greenish"
            />
          )}
        </Stack>

        {customFields.map((field, index) => (
          <TextFieldRequired
            key={field.identity}
            control={control}
            borderRadius={16}
            size="small"
            fieldName={`customFields.${index}.value`}
            label={field.name}
            placeholder=""
            inputProps={{
              "data-cy": field.identity,
              "data-testid": field.identity,
            }}
            dataCyErrorLabel={field.identity}
            clearErrors={clearErrors}
            validate={(value) => (value ?? "") !== ""}
            onChangeValue={(value) => {
              setValue(`customFields.${index}`, {
                identity: field.identity,
                name: field.name,
                value,
              })
            }}
            scheme="greenish"
          />
        ))}
      </Stack>
    </Paper>
  )
}

export default CheckoutClient8
